import { Box, Collapse, LinearProgress, Typography } from '@mui/material';
import { Trash } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import Label from '../../../components/UI/Label/Label';
import { UploadService } from '../../../service/UploadService';

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
  fileUrl?: string;
  response?: any;
  isExtraction: boolean;
}

export function Dropzone({
  data,
  nd,
  input,
  setInputs,
}: any) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadedFilesCollapsed, setUploadedFilesCollapsed] = useState(true);
  const nodeId = nd.data.selectedFlow;

  const isVarInputNode = (nodeId: string) => {
    return nodeId.startsWith('varInputNode');
  };

  useEffect(() => {
    if (nodeId && isVarInputNode(nodeId)) {
      const existingInput = input?.find((inp: any) => inp.inputId === nodeId);

      if (!existingInput) {
        const isExtractionMode =
          nd.data.ruleVarInputNode === 'both' ||
          nd.data.ruleVarInputNode === 'upload';

        setInputs((prev: any) => [
          ...(prev || []),
          {
            inputId: nodeId,
            inputText: '',
            links: [],
            isExtraction: isExtractionMode,
          },
        ]);
      }
    }
  }, []);

  useEffect(() => {
    if (input && nodeId && isVarInputNode(nodeId)) {
      const currentInput = input.find((inp: any) => inp.inputId === nodeId);

      if (currentInput?.links?.length > 0) {
        setUploadedFiles(prev =>
          prev.filter(file => !file.id.startsWith('existing-')),
        );

        const existingFiles = currentInput.links.map(
          (link: any, index: number) => ({
            id: `existing-${link.name}-${index}`,
            name: link.name,
            progress: 100,
            status: 'complete' as const,
            fileUrl: link.url,
          }),
        );

        setUploadedFiles(prev => {
          const newFiles = existingFiles.filter(
            (newFile: any) =>
              !prev.some(
                existing =>
                  existing.name === newFile.name &&
                  existing.fileUrl === newFile.fileUrl,
              ),
          );
          return [...prev, ...newFiles];
        });
      }
    }
  }, [input, nodeId]);

  const getAcceptedFileTypes = (fileTypes: string[]) => {
    const acceptObject = {} as Accept;
    
    if (fileTypes?.includes('PDF')) {
      acceptObject['application/pdf'] = ['.pdf'];
    }

    if (fileTypes?.includes('TXT')) {
      acceptObject['text/plain'] = ['.txt'];
    }

    if (fileTypes?.includes('DOCX')) {
      acceptObject['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] = ['.docx'];
    }

    if (fileTypes?.includes('JPG') || fileTypes?.includes('JPEG')) {
      acceptObject['image/jpeg'] = ['.jpg', '.jpeg'];
    }

    if (fileTypes?.includes('PNG')) {
      acceptObject['image/png'] = ['.png'];
    }

    if (fileTypes?.includes('PPTX')) {
      acceptObject['application/vnd.openxmlformats-officedocument.presentationml.presentation'] = ['.pptx'];
    }

    if (Object.keys(acceptObject).length === 0) {
      return {
        'application/pdf': ['.pdf'],
        'text/plain': ['.txt'],
      };
    }

    return acceptObject;
  };

  const onDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
  ) => {
    const newFiles = acceptedFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      name: file.name,
      progress: 0,
      status: 'uploading' as const,
      isExtraction: false,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
    for (const fileData of newFiles) {
      await uploadFile(fileData);
    }
  };

  const uploadFile = async (fileData: UploadedFile) => {
    try {
      const formData = new FormData();
      formData.append('file', fileData.file);

      const progressInterval = setInterval(() => {
        setUploadedFiles(prev =>
          prev.map(f => {
            if (f.id === fileData.id && f.progress < 90) {
              return { ...f, progress: f.progress + 10 };
            }
            return f;
          }),
        );
      }, 500);

      const response = await UploadService.uploadFile(formData);

      clearInterval(progressInterval);

      updateFileWithResponse(fileData.id, response);
    } catch (error) {
      console.error('Upload error:', error);
      updateFileStatus(
        fileData.id,
        'error',
        error instanceof Error ? error.message : 'Upload failed',
      );
    }
  };

  const updateFileStatus = (
    id: string,
    status: 'uploading' | 'complete' | 'error',
    error?: string,
  ) => {
    setUploadedFiles(prev =>
      prev.map(file => {
        if (file.id === id) {
          return {
            ...file,
            status,
            progress: status === 'complete' ? 100 : file.progress,
            error,
          };
        }
        return file;
      }),
    );
  };

  const updateFileWithResponse = (id: string, response: any) => {
    const fileUrl = response?.data?.src || '';
    const fileName = response?.data?.name || '';

    setUploadedFiles(prev => {
      const fileExists = prev.find(file => file.id === id);

      if (!fileExists) return prev;

      return prev.map(file => {
        if (file.id === id) {
          return {
            ...file,
            status: 'complete',
            progress: 100,
            name: fileName || file.name,
            fileUrl,
            response,
            isExtraction: false,
          };
        }
        return file;
      });
    });

    if (setInputs && nodeId && isVarInputNode(nodeId)) {
      setTimeout(() => {
        updateInputWithFile(
          nodeId,
          fileName || response?.data?.originalname || id.split('-')[0],
          fileUrl,
        );
      }, 0);
    }
  };

  const updateInputWithFile = (
    nodeId: string,
    fileName: string,
    fileUrl: string,
  ) => {
    if (!isVarInputNode(nodeId)) return;

    setInputs((prev: any) => {
      return prev.map((inp: any) => {
        if (inp.inputId === nodeId) {
          const newLink = {
            name: fileName,
            url: fileUrl,
          };

          const linkExists = inp.links.some(
            (link: any) =>
              link.name === newLink.name && link.url === newLink.url,
          );

          if (!linkExists) {
            const isExtractionMode =
              nd.data.ruleVarInputNode === 'both' ||
              nd.data.ruleVarInputNode === 'upload';

            return {
              ...inp,
              links: [...inp.links, newLink],
              isExtraction: isExtractionMode,
            };
          }
        }
        return inp;
      });
    });
  };

  const removeFile = (id: string) => {
    const fileToRemove = uploadedFiles.find(file => file.id === id);

    if (fileToRemove && nodeId && isVarInputNode(nodeId)) {
      removeFileFromInput(nodeId, fileToRemove.name, fileToRemove.fileUrl);
    }

    setUploadedFiles(prev => prev.filter(file => file.id !== id));
  };

  const removeFileFromInput = (
    nodeId: string,
    fileName: string,
    fileUrl?: string,
  ) => {
    if (!isVarInputNode(nodeId) || !setInputs) return;

    setInputs((prev: any) => {
      return prev.map((inp: any) => {
        if (inp.inputId === nodeId) {
          const isExtractionMode =
            nd.data.ruleVarInputNode === 'both' ||
            nd.data.ruleVarInputNode === 'upload';

          return {
            ...inp,
            links: inp.links.filter((link: any) => {
              if (fileUrl) {
                return !(link.name === fileName && link.url === fileUrl);
              }
              return link.name !== fileName;
            }),
            isExtraction: isExtractionMode,
          };
        }
        return inp;
      });
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: getAcceptedFileTypes(nd.data.fileTypesSelected),
    onDrop,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: data?.bgColor || 'white',
        width: '100%',
      }}
    >
      <Box
        sx={{
          border: '1px dashed #98A2B3',
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
          color: '#98A2B3',
          width: '100%',
        }}
      >
        <div
          {...getRootProps({ className: 'dropzone' })}
          style={{
            width: '100%',
            padding: '16px',
            textAlign: 'center',
            cursor: 'pointer',
          }}
        >
          <input {...getInputProps()} disabled={nodeId === ''} />
          <p>
            {isDragActive
              ? 'Drop the files here...'
              : `Drag 'n' drop ${
                  nd.data.fileTypesSelected?.join(', ') || 'PDF, TXT'
                } files here, or click to select files`}
          </p>
        </div>
      </Box>

      {uploadedFiles.length > 0 && (
        <>
          <Label
            labelName="Uploaded Files"
            collapsed={uploadedFilesCollapsed}
            onCollapse={() =>
              setUploadedFilesCollapsed(!uploadedFilesCollapsed)
            }
          />

          <Collapse in={uploadedFilesCollapsed}>
            <Box
              display="flex"
              flexDirection="column"
              gap="8px"
              sx={{
                width: '100%',
              }}
            >
              {uploadedFiles.map(file => (
                <Box
                  key={file.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#F9FAFB',
                    padding: '16px',
                    borderRadius: '8px',
                    gap: '4px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        color: '#101828',
                      }}
                    >
                      {file.name}
                    </span>

                    {file.status === 'complete' && (
                      <Trash
                        size={16}
                        color="#F05450"
                        cursor="pointer"
                        onClick={() => removeFile(file.id)}
                      />
                    )}
                  </Box>

                  {file.status === 'uploading' && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={file.progress}
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >{`${Math.round(file.progress)}%`}</Typography>
                      </Box>
                    </Box>
                  )}

                  {file.status === 'error' && (
                    <Typography variant="body2" sx={{ color: '#F05450' }}>
                      Error: {file.error || 'Upload failed'}
                    </Typography>
                  )}

                  {file.status === 'complete' && file.fileUrl && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#0B5CFF',
                          fontSize: '12px',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={() => window.open(file.fileUrl, '_blank')}
                      >
                        View file
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
}
